/**
 * Cart Gift Sync - keeps each offer's free gifts at 1:1 with its buy products.
 *
 * The store now has separate hidden gift pools for:
 * - Bettwaesche-set 2 fuer 1
 * - Bettdecke + Bettwaesche-set gratis
 *
 * Syncing globally is unsafe in mixed carts, so this reconciles per hidden gift
 * handle group. It also gates checkout while a needed sync is still running.
 */
class CartGiftSync extends HTMLElement {
  connectedCallback() {
    var dataEl = document.getElementById(this.getAttribute('data-id'));
    if (!dataEl) return;

    try {
      this._data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this._boundHandleCheckoutSubmit = this._handleCheckoutSubmit.bind(this);
    this._checkoutForm = document.getElementById('mini-cart-form');
    if (this._checkoutForm) {
      this._checkoutForm.addEventListener('submit', this._boundHandleCheckoutSubmit, true);
    }

    this._syncing = this._run();
  }

  disconnectedCallback() {
    if (this._checkoutForm && this._boundHandleCheckoutSubmit) {
      this._checkoutForm.removeEventListener('submit', this._boundHandleCheckoutSubmit, true);
    }
  }

  _routes() {
    var r = (window.themeVariables && window.themeVariables.routes) || {};
    return {
      add: r.cartAddUrl || '/cart/add.js',
      update: r.cartUpdateUrl || '/cart/update.js'
    };
  }

  _locked() { return (Date.now() - (window.__cozyGiftSyncLock || 0)) < 8000; }
  _lock() { window.__cozyGiftSyncLock = Date.now(); }
  _unlock() { window.__cozyGiftSyncLock = 0; }

  _groupKey(handles) {
    return (handles || []).slice().sort().join('|');
  }

  _buildGroups() {
    var lines = this._data.lines || [];
    var spendIds = this._data.spendOfferIds || [];
    var groupsByKey = {};
    var groups = [];
    var orphanGiftLines = [];

    // 1. Buy lines -> one group each. A group is keyed by the offer's own id
    // (`buyOfferId`) when present, else by its gift-handle set (legacy). The group
    // also carries `giftsPerBuy` so multi-gift offers (e.g. the couple bundle = 2
    // pillows per set) aren't capped to 1. See GIFT-OFFERS.md.
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var handles = line.offerGiftHandles || [];
      if (line.isGift || handles.length === 0) continue;

      var key = line.buyOfferId || this._groupKey(handles);
      if (!groupsByKey[key]) {
        groupsByKey[key] = {
          key: key,
          offerId: line.buyOfferId || null,
          buyQty: 0,
          giftsPerBuy: line.giftsPerBuy || 1,
          giftHandles: handles,
          giftLines: [],
          defaultGiftVariantId: null,
          defaultGiftAvailable: false
        };
        groups.push(groupsByKey[key]);
      }
      groupsByKey[key].buyQty += line.qty || 0;
      if (line.giftsPerBuy) groupsByKey[key].giftsPerBuy = line.giftsPerBuy;
    }

    // 2. Gift lines -> their group. Tagged gifts (`giftOfferId`) belong ONLY to the
    // buy group with the same offer id. A tagged gift with no matching buy group is
    // either a spend/campaign gift (leave it alone — the campaign module owns it) or
    // a buy gift whose buy line is gone (remove it). Untagged gifts use the legacy
    // handle match so the existing BOGO-set flow is unchanged.
    for (var g = 0; g < lines.length; g++) {
      var giftLine = lines[g];
      if (!giftLine.isGift) continue;

      var oid = giftLine.giftOfferId;
      var group = null;

      if (oid) {
        group = groupsByKey[oid] || null;
        if (!group) {
          if (spendIds.indexOf(oid) !== -1) continue; // campaign/spend gift — not ours
          orphanGiftLines.push({ key: giftLine.key, qty: giftLine.qty || 0 });
          continue;
        }
      } else {
        for (var k = 0; k < groups.length; k++) {
          if (groups[k].giftHandles.indexOf(giftLine.productHandle) !== -1) { group = groups[k]; break; }
        }
        if (!group) { orphanGiftLines.push({ key: giftLine.key, qty: giftLine.qty || 0 }); continue; }
      }

      group.giftLines.push({
        key: giftLine.key,
        qty: giftLine.qty || 0,
        variantId: giftLine.variantId,
        variantAvailable: giftLine.variantAvailable
      });

      if (!group.defaultGiftVariantId) {
        group.defaultGiftVariantId = giftLine.variantId;
        group.defaultGiftAvailable = giftLine.variantAvailable;
      }
    }

    return { groups: groups, orphanGiftLines: orphanGiftLines };
  }

  _plan() {
    var built = this._buildGroups();
    var groups = built.groups;
    var operations = [];
    var MAX = 10;

    // Remove orphaned gifts (their buy line is gone) regardless of other groups.
    if (built.orphanGiftLines.length > 0) {
      operations.push({ type: 'remove', giftLines: built.orphanGiftLines, qty: built.orphanGiftLines.reduce(function (n, l) { return n + l.qty; }, 0) });
    }

    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var currentGiftQty = group.giftLines.reduce(function (n, l) { return n + l.qty; }, 0);
      // Entitlement = bought units × gifts-per-buy (couple bundle = 2), capped.
      var desiredGiftQty = Math.min((group.buyQty || 0) * (group.giftsPerBuy || 1), MAX);

      if (currentGiftQty === desiredGiftQty) continue;

      if (currentGiftQty < desiredGiftQty) {
        if (group.giftLines.length === 0 || !group.defaultGiftVariantId || !group.defaultGiftAvailable) continue;
        operations.push({
          type: 'add',
          variantId: group.defaultGiftVariantId,
          offerId: group.offerId,
          qty: desiredGiftQty - currentGiftQty
        });
      } else {
        operations.push({
          type: 'remove',
          giftLines: group.giftLines,
          qty: currentGiftQty - desiredGiftQty
        });
      }
    }

    return operations;
  }

  _run() {
    var operations = this._plan();
    if (operations.length === 0) return Promise.resolve(false);

    if (this._locked()) {
      return window.__cozyGiftSyncPromise || Promise.resolve(false);
    }

    return this._executeOperations(operations);
  }

  _executeOperations(operations) {
    this._lock();

    var self = this;
    var updates = {};
    var addItems = [];

    for (var i = 0; i < operations.length; i++) {
      var op = operations[i];

      if (op.type === 'add') {
        var addProps = { _free_gift: 'true' };
        if (op.offerId) addProps._offer_id = op.offerId;
        addItems.push({ id: op.variantId, quantity: op.qty, properties: addProps });
        continue;
      }

      if (op.type === 'remove') {
        var remaining = op.qty;
        for (var g = op.giftLines.length - 1; g >= 0 && remaining > 0; g--) {
          var giftLine = op.giftLines[g];
          var take = Math.min(giftLine.qty, remaining);
          updates[giftLine.key] = giftLine.qty - take;
          remaining -= take;
        }
      }
    }

    var routes = this._routes();
    var promise = Promise.resolve()
      .then(function () {
        if (Object.keys(updates).length === 0) return null;
        return fetch(routes.update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) {
          if (!r.ok) throw new Error('gift update failed');
          return r.json();
        });
      })
      .then(function () {
        if (addItems.length === 0) return null;
        return fetch(routes.add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: addItems })
        }).then(function (r) {
          if (!r.ok) throw new Error('gift add failed');
          return r.json();
        });
      })
      .then(function () {
        self._finish();
        return true;
      })
      .catch(function (err) {
        self._unlock();
        console.error('[CartGiftSync]', err);
        return false;
      });

    window.__cozyGiftSyncPromise = promise;
    return promise;
  }

  _finish() {
    this._unlock();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  _handleCheckoutSubmit(event) {
    var operations = this._plan();
    if (operations.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var promise = (this._locked() && window.__cozyGiftSyncPromise)
      ? window.__cozyGiftSyncPromise
      : this._executeOperations(operations);

    promise.then(function () {
      window.location.href = '/checkout';
    });
  }
}

if (!customElements.get('cart-gift-sync')) {
  customElements.define('cart-gift-sync', CartGiftSync);
}
